import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import NativeModal from 'react-modal';
import { Trans } from 'react-i18next';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { registrationRequest as registrationRequestAct } from '@xanadu/ui-shared/modules/userRegistration/actions';

import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { isFuturesOrPredictionsProduct } from '@xanadu/ui-shared/modules/application/selectors';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';

import Button from '@xanadu/ui-shared/components/widgets/Button';

import {
  startTourMode as startTourModeAct,
  toggleCustomerOnboardingPopup as toggleCustomerOnboardingPopupAct,
} from '../../modules/customerOnboarding/actions';

import { getClientStyledText } from '../../utils';

import CustomerOnboardingCards from '../CustomerOnboardingCards';

import IconClose from '../../../../images/icons/misc/close.svg';
import IconCollapse from '../../../../images/icons/misc/icon-videocollapse.svg';
import IconPlayButton from '../../../../images/icons/misc/icon-playbutton.svg';

import styles from './CustomerOnboarding.module.scss';

NativeModal.setAppElement(document.getElementById('root'));

const CustomerOnboarding = ({
  widthSize,
  clientName,
  clientSite,
  isFuturesOrPredictions,
  isLoggedIn,
  startTourMode,
  firstTimeVisitor,
  onboardingBonusCode,
  registrationRequest,
  toggleCustomerOnboardingPopup,
  forceTour = false, // this is used to be able to start the tour straight-away
  history: { push: navigate },
}) => {
  const {
    featuresFlags: { enableTakeTour = true, } = {},
    customization: {
      CustomerOnBoarding: {
        title = '',
        textContent = [],
        // right now the layout only supports one video
        videoContent = [],
      } = {},
    } = {},
  } = useContext(ChildrenPropsContext) || {};

  const { t } = useImprovedTranslation();
  const [prepareForMobileTour, setPrepareForMobileTour] = useState(false);
  const [showMobileTour, setShowMobileTour] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');

  const includesVideo = videoContent.length > 0;

  const isMobile = useMemo(() => widthSize < 1200, [widthSize]);

  const handleClose = () => {
    toggleCustomerOnboardingPopup({
      visible: false,
      firstTimeVisitor: false
    });
  };

  const onCloseButtonClick = () => {
    if (selectedVideoUrl && !isMobile) {
      setSelectedVideoUrl('');
      return;
    }

    handleClose();
    track('Marketing', 'Onboarding closed');
  };

  const handleTour = () => {
    if (firstTimeVisitor) {
      track('Marketing', 'Tour Start from Popup');
    } else {
      track('Marketing', 'Tour Start from RHN');
    }

    if (isMobile) {
      setPrepareForMobileTour(true);

      setTimeout(() => {
        setPrepareForMobileTour(false);
        setShowMobileTour(true);
      }, 270);

      return;
    }

    handleClose();

    setTimeout(() => {
      startTourMode();
    }, 100);
  };

  const onVideoSelect = (url) => () => {
    track('Marketing', 'Onboarding video selected');
    setSelectedVideoUrl(url);
  };

  const handleSignUp = () => {
    handleClose();

    setTimeout(() => {
      navigate(`/registration${onboardingBonusCode ? `?bonus-code=${onboardingBonusCode}` : ''}`);

      track('Marketing', 'Reg opened from Tour');
    }, 300);
  };

  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.overflow = 'hidden';
    }

    return () => {
      if (isMobile) {
        document.documentElement.style.overflow = '';
      }
    };
  }, [isMobile]);

  useEffect(() => {
    track('Marketing', 'Onboarding opened');

    return () => {
      try {
        localStorage.setItem('mb-onboarding-visited', true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Error accessing localStorage', e.message);
      }
    };
  }, []);

  useEffect(() => {
    if (forceTour) {
      handleTour();
    }
  }, [forceTour]);

  const styledSpan = getClientStyledText(clientName, clientSite, { size: 'inherit' });

  const modalClass = classNames({
    'react-modal': true,
    'react-modal--center': true,
    [styles.modal]: true,
  });

  const bodyOpenClass = classNames({
    'ReactModal__Body--open': true,
    'ReactModal__Body--open--withScroll': true,
  });

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--mobile']]: isMobile,
    [styles['main--withVideo']]: includesVideo,
    [styles['main--videoSelected']]: Boolean(selectedVideoUrl) && !isMobile,
  });

  const videoListClass = classNames({
    [styles.videoList]: true,
    [styles['videoList--single']]: videoContent.length === 1,
  });

  return (
    <NativeModal
      isOpen
      className={modalClass}
      bodyOpenClassName={bodyOpenClass}
      overlayClassName="mb-modal__overlay"
      shouldCloseOnOverlayClick
    >
      {prepareForMobileTour ? null : (
        <Fragment>
          {showMobileTour ? (
            <CustomerOnboardingCards
              isFuturesOrPredictions={isFuturesOrPredictions}
              isLoggedIn={isLoggedIn}
              registrationRequest={registrationRequest}
              toggleCustomerOnboardingPopup={toggleCustomerOnboardingPopup}
            />
          ) : (
            <div className={mainClass}>
              <button
                className={styles.close}
                onClick={onCloseButtonClick}
              >
                {selectedVideoUrl && !isMobile ? (
                  <IconCollapse />
                ) : (
                  <IconClose />
                )}
              </button>
              {selectedVideoUrl && !isMobile ? (
                <iframe
                  width="100%"
                  height="100%"
                  className={styles.videoIframe}
                  src={`${selectedVideoUrl}?autoplay=1`}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <Fragment>
                  <div className={styles.content}>
                    <h2 className={styles.title}>
                      {title || (
                        <Fragment>
                          {firstTimeVisitor ? (
                            <Fragment>
                              <span>{t('Welcome to')}</span>
                              {styledSpan}
                            </Fragment>
                          ) : (
                            <Fragment>
                              <span>{t('Explore')}</span>
                              {styledSpan}
                            </Fragment>
                          )}
                        </Fragment>
                      )}
                    </h2>
                    {textContent.map(text => (
                      <p key={text}>
                        {t(text)}
                      </p>
                    ))}
                    {includesVideo && (
                      <div className={videoListClass}>
                        {videoContent.map((video) => (
                          <div
                            key={video.url}
                            role="button"
                            className={styles.video}
                            onClick={onVideoSelect(video.url)}
                          >
                            {selectedVideoUrl === video.url && isMobile ? (
                              <iframe
                                width="100%"
                                height="100%"
                                className={styles.videoIframe}
                                src={`${selectedVideoUrl}?autoplay=1`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                              />
                            ) : (
                              <div className={styles.videoImage}>
                                <span style={{ backgroundImage: `url(${video.placeholder})` }} />
                                <i className={styles.videoImageIcon}>
                                  <IconPlayButton />
                                </i>
                              </div>
                            )}
                            {video.title && (
                              <div className={styles.videoTitle}>
                                {t(video.title)}
                              </div>
                            )}
                            {video.description && (
                              <span className={styles.videoDescription}>
                                {t(video.description)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={styles.actions}>
                    {firstTimeVisitor ? (
                      <Fragment>
                        {enableTakeTour && (
                          <Button
                            sizeType="medium"
                            buttonType="plain-white"
                            className={styles.secondaryButton}
                            onClick={handleTour}
                          >{t('Take a Tour')}</Button>
                        )}
                        <Button
                          sizeType="medium"
                          buttonType="primary"
                          className={styles.primaryButton}
                          onClick={handleSignUp}
                        >
                          <Trans
                            i18nKey="Join brand"
                          >
                            <span>Join {styledSpan}</span>
                          </Trans>
                        </Button>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <Button
                          sizeType="medium"
                          buttonType="plain-white"
                          className={styles.secondaryButton}
                          onClick={handleClose}
                        >{t('Close')}</Button>
                        {enableTakeTour && (
                          <Button
                            sizeType="medium"
                            buttonType="primary"
                            className={styles.primaryButton}
                            onClick={handleTour}
                          >{t('Take a Tour')}</Button>
                        )}
                      </Fragment>
                    )}
                  </div>
                </Fragment>
              )}
            </div>
          )}
        </Fragment>
      )}
    </NativeModal>
  );
};

CustomerOnboarding.propTypes = {
  widthSize: PropTypes.number.isRequired,
  clientName: PropTypes.string.isRequired,
  clientSite: PropTypes.string.isRequired,
  isFuturesOrPredictions: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  firstTimeVisitor: PropTypes.bool.isRequired,
  forceTour: PropTypes.bool.isRequired,
  onboardingBonusCode: PropTypes.string,
  startTourMode: PropTypes.func.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  toggleCustomerOnboardingPopup: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
};

const mapDispatchToProps = {
  startTourMode: startTourModeAct,
  registrationRequest: registrationRequestAct,
  toggleCustomerOnboardingPopup: toggleCustomerOnboardingPopupAct,
  setHighlightedSidebarButton: () => { }
};

function mapStateToProps(state) {
  return {
    widthSize: state.getIn(['application', 'widthSize']),
    clientName: state.getIn(['application', 'clientName']),
    clientSite: state.getIn(['application', 'site']),
    onboardingBonusCode: state.getIn(['application', 'onboardingBonusCode']),
    isFuturesOrPredictions: isFuturesOrPredictionsProduct(state),
    isLoggedIn: state.getIn(['user', 'isLoggedIn']),
    firstTimeVisitor: state.getIn(['local', 'customerOnboarding', 'customerOnboardingPopupConfig', 'firstTimeVisitor']),
    forceTour: state.getIn(['local', 'customerOnboarding', 'customerOnboardingPopupConfig', 'forceTour']),
  };
}

export const Component = CustomerOnboarding;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerOnboarding));
